/* Shopify renders all cart amounts and lines; mutations always use canonical IDs/line keys. */
(() => {
  const drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer || typeof drawer.showModal !== 'function' || !window.fetch) return;
  const content = drawer.querySelector('[data-cart-content]');
  const status = drawer.querySelector('[data-cart-status]');
  const error = drawer.querySelector('[data-cart-error]');
  const closeButton = drawer.querySelector('[data-cart-close]');
  const section = drawer.dataset.sectionId;
  const cartUrl = drawer.dataset.cartUrl;
  let busy = false;
  let opener;
  let ready = true;
  let changed = false;
  const syncCartPage = () => {
    if (changed && !busy && !drawer.open && document.querySelector('[data-main-cart]')) location.reload();
  };
  const setError = message => {
    error.hidden = !message;
    error.querySelector('[data-cart-error-message]').textContent = message || '';
  };
  const checkoutState = () => drawer.querySelectorAll('[name="checkout"]').forEach(button => { button.disabled = busy || !ready; });
  const open = target => {
    if (!drawer.open) {
      opener = target || document.activeElement;
      drawer.showModal();
      document.documentElement.classList.add('cart-is-open');
      closeButton.focus();
    }
  };
  const render = html => {
    if (typeof html !== 'string') throw new Error('Missing cart section');
    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-content]');
    if (!next || !/^\d+$/.test(next.dataset.itemCount)) throw new Error('Invalid cart section');
    content.replaceChildren(...next.childNodes);
    content.dataset.itemCount = next.dataset.itemCount;
    document.querySelectorAll('[data-cart-count], [data-drawer-count]').forEach(node => { node.textContent = next.dataset.itemCount; });
    document.querySelectorAll('[data-cart-open]').forEach(link => { link.setAttribute('aria-label', `Bag, ${next.dataset.itemCount} items`); });
    ready = true;
    checkoutState();
  };
  const refresh = async () => {
    const response = await fetch(`${cartUrl}?section_id=${encodeURIComponent(section)}`, {credentials:'same-origin', cache:'no-store'});
    if (!response.ok) throw new Error('Cart unavailable');
    render(await response.text());
  };
  const run = async (operation, pendingMessage) => {
    if (busy) return;
    busy = true;
    const controls = [...drawer.querySelectorAll('[data-cart-adjust], [data-cart-remove]')].map(node => [node, node.disabled]);
    controls.forEach(([node]) => { node.disabled = true; });
    content.setAttribute('aria-busy', 'true');
    checkoutState();
    status.textContent = pendingMessage;
    setError('');
    try { await operation(); }
    catch {
      ready = false;
      // A failed response can still mean the server changed the bag. Read back; never repeat the write.
      try { await refresh(); } catch { /* The full cart remains a recovery route. */ }
      status.textContent = '';
      setError(ready ? 'The request could not be confirmed. Check your bag before trying again.' : 'Your bag could not be refreshed. Open your bag to check it before checkout.');
    } finally {
      busy = false;
      controls.forEach(([node, disabled]) => { if (node.isConnected) node.disabled = disabled; });
      content.removeAttribute('aria-busy');
      checkoutState();
      syncCartPage();
    }
  };
  const mutate = async (url, body, message) => {
    changed = true;
    const response = await fetch(`${url}.js`, {method:'POST', credentials:'same-origin', headers:{Accept:'application/json', 'Content-Type':'application/json'}, body:JSON.stringify({...body, sections:section, sections_url:cartUrl})});
    const data = await response.json();
    if (!response.ok) {
      await refresh();
      status.textContent = '';
      setError(typeof data.description === 'string' ? data.description : 'This item could not be updated. Please check the available quantity.');
      return;
    }
    try { render(data.sections?.[section]); } catch { await refresh(); }
    status.textContent = message;
  };
  document.querySelectorAll('[data-cart-open]').forEach(link => {
    link.setAttribute('aria-haspopup', 'dialog');
    link.setAttribute('aria-controls', drawer.id);
  });
  document.addEventListener('click', event => {
    const link = event.target.closest('[data-cart-open]');
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
    event.preventDefault();
    open(link);
    run(async () => { await refresh(); status.textContent = ''; }, 'Updating your bag…');
  });
  drawer.addEventListener('click', event => {
    if (event.target.closest('[data-cart-close]') || event.target === drawer) { drawer.close(); return; }
    const button = event.target.closest('[data-cart-adjust], [data-cart-remove]');
    if (!button || busy || button.disabled) return;
    const line = button.closest('[data-cart-line]');
    const step = Number(button.dataset.step || 1);
    const remove = button.hasAttribute('data-cart-remove');
    const quantity = remove ? 0 : Number(line.dataset.quantity) + (button.dataset.cartAdjust === 'plus' ? step : -step);
    if (!Number.isInteger(quantity) || quantity < 0) return;
    const action = button.dataset.cartAdjust;
    const key = line.dataset.lineKey;
    run(async () => {
      await mutate(drawer.dataset.changeUrl, {id:key, quantity}, remove ? 'Item removed from your bag.' : 'Quantity updated.');
      const updatedLine = [...drawer.querySelectorAll('[data-cart-line]')].find(node => node.dataset.lineKey === key);
      const focusTarget = [...(updatedLine?.querySelectorAll('[data-cart-adjust]') || [])].find(node => node.dataset.cartAdjust === action && !node.disabled);
      if (drawer.open) (focusTarget || closeButton).focus();
    }, 'Updating your bag…');
  });
  drawer.addEventListener('close', () => {
    document.documentElement.classList.remove('cart-is-open');
    if (opener?.isConnected) opener.focus();
    syncCartPage();
  });
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!form.matches('[data-card-form], [data-purchase-form]') || event.defaultPrevented) return;
    if (!form.checkValidity()) return;
    const values = new FormData(form);
    const id = values.get('id');
    const quantity = Number(values.get('quantity') || 1);
    if (!/^\d+$/.test(id || '') || !Number.isInteger(quantity) || quantity < 1 || event.submitter?.disabled) return;
    event.preventDefault();
    if (busy) return;
    // Preserve selling plans and public/custom line properties from the native form.
    const item = {id, quantity};
    if (values.get('selling_plan')) item.selling_plan = values.get('selling_plan');
    const properties = {};
    for (const [name, value] of values) {
      const match = name.match(/^properties\[(.+)\]$/);
      if (match && typeof value === 'string') properties[match[1]] = value;
    }
    if (Object.keys(properties).length) item.properties = properties;
    open(event.submitter);
    run(() => mutate(drawer.dataset.addUrl, {items:[item]}, 'Added to your bag.'), 'Adding to your bag…');
  });
})();
